/**
 * Cliente tipado da API `/v1` — o que `apps/web` importa.
 *
 * ── Por que ele existe aqui, e não em cada aplicação ──────────────────────
 *
 * A regra r3 do dependency-cruiser (§17.1) proíbe `apps/web` de importar
 * adapter: se importasse, nome de campo do Twenty ou do Chatwoot atravessaria
 * até a tela e o princípio 01 morreria. Para essa regra ser cumprível, precisa
 * existir um caminho legítimo — este.
 *
 * ── Por que não gerar código a partir da OpenAPI ──────────────────────────
 *
 * Geraria um terceiro artefato para manter sincronizado, e o ponto todo da B33
 * é ter uma fonte só. As schemas já são TypeScript; o cliente as usa direto, e
 * o tipo da resposta é inferido da mesma schema que o servidor valida.
 *
 * O que a H02 pede de "cliente gerado" é o EFEITO: *"quebra de contrato aparece
 * em compilação, não em produção"*. Aqui isso vale mais forte que num gerador —
 * corpo, resposta, parâmetro de caminho e nome de parâmetro de consulta saem
 * todos da declaração em `rotas.ts`. Mudar um caminho de `{id}` para `{uuid}`
 * quebra a compilação de quem chama, sem passo de geração no meio.
 */

import type { z } from 'zod';
import { ROTAS, type Rota } from './v1/rotas.js';
import { ProblemaSchema, type Problema } from './v1/problema.js';

/**
 * O cabeçalho de correlação (§7).
 *
 * Mesmo valor de `CABECALHO_DE_CORRELACAO` em `@cliqcrm/observability`, e não
 * um import: `apps/web` só pode importar este pacote (r3), então a constante
 * precisa existir aqui. Um teste em `apps/api` — que enxerga os dois lados —
 * garante que não divirjam.
 */
export const CABECALHO_DE_CORRELACAO = 'x-correlation-id';

/** `Idempotency-Key` (§6.1, B30). Exigido por comando com efeito duplicável. */
export const CABECALHO_DE_IDEMPOTENCIA = 'idempotency-key';

/**
 * Erro do servidor, já no formato da §6.1.
 *
 * `codigo` e `retryable` ficam expostos porque são o que o chamador usa para
 * decidir: repetir, pedir login de novo, ou mostrar o campo recusado. Ler isso
 * de `mensagem` seria comparar texto, e texto muda com revisão de português.
 *
 * `correlationId` sobe junto porque a H03 exige mostrá-lo na tela de erro — é o
 * que leva o suporte direto ao log, sem o usuário precisar descrever o que fez.
 */
export class ErroDaApi extends Error {
  readonly codigo: string;
  readonly retryable: boolean;
  readonly correlationId: string;

  constructor(
    readonly status: number,
    readonly problema: Problema,
    /**
     * Quanto o servidor pediu para esperar antes de tentar de novo (cabeçalho
     * `Retry-After`, em segundos), ou `null` quando ele não disse. É o que o
     * 429 do limite de requisições manda: tentar antes disso só gasta mais
     * uma tentativa na mesma recusa.
     */
    readonly esperarSegundos: number | null = null,
  ) {
    super(`${problema.code}: ${problema.title}`);
    this.name = 'ErroDaApi';
    this.codigo = problema.code;
    this.retryable = problema.retryable;
    this.correlationId = problema.correlation_id;
  }
}

/** O `Retry-After` em segundos, se for um número que faça sentido. */
function esperaPedida(cabecalho: string | null): number | null {
  if (!cabecalho) return null;
  const segundos = Number(cabecalho);
  return Number.isFinite(segundos) && segundos >= 0 ? segundos : null;
}

/**
 * A rede não respondeu — diferente de o servidor ter recusado.
 *
 * A distinção importa para a tela: `ErroDaApi` tem `correlation_id` e uma
 * mensagem do catálogo; isto aqui não tem nem um nem outro, e insistir é quase
 * sempre a resposta certa. Tratar os dois como o mesmo erro produziria uma tela
 * pedindo para o usuário passar um código que não existe.
 */
export class ErroDeRede extends Error {
  readonly retryable = true;

  constructor(
    readonly rota: string,
    causa: unknown,
  ) {
    super(`Não foi possível falar com o servidor (${rota}).`);
    this.name = 'ErroDeRede';
    this.cause = causa;
  }
}

export interface OpcoesDoCliente {
  /** Origem da API. Vazio usa a mesma origem da página. */
  baseUrl?: string;
  /**
   * Devolve o token de acesso. Função, e não string, de propósito: o token dura
   * 15 minutos (ADR-0006) e é renovado em segundo plano. Guardar o valor faria
   * o cliente segurar um token vencido pelo resto da sessão.
   */
  token: () => string | Promise<string>;
  /** Injetável para teste. */
  fetch?: typeof globalThis.fetch;
  /**
   * Gera o `Correlation-Id` de cada requisição.
   *
   * Gerado no CLIENTE, e não esperado do servidor: assim o identificador existe
   * antes de a requisição sair, e uma falha de rede — que nunca chega ao
   * servidor — também tem um id para o log do navegador. Injetável para o teste
   * poder afirmar o valor.
   */
  novaCorrelacao?: () => string;
  /**
   * Chamado ao fim de toda chamada, com ou sem erro.
   *
   * É o gancho de observabilidade do lado do cliente: quem quiser registrar
   * latência ou guardar o último `correlation_id` para a tela de erro pluga
   * aqui, sem o cliente precisar conhecer log nem métrica.
   */
  aoResponder?: (registro: RegistroDaChamada) => void;
}

export interface RegistroDaChamada {
  readonly rota: string;
  readonly metodo: string;
  readonly caminho: string;
  readonly status: number | null;
  readonly correlationId: string;
  readonly duracaoMs: number;
  readonly erro?: unknown;
}

// ── Tipos derivados da declaração da rota ─────────────────────────────────
//
// O que segue extrai, do próprio `rotas.ts`, quais parâmetros de caminho e de
// consulta cada rota aceita. É o que faz `{id}` ser obrigatório e `?curso=`
// (em vez de `?cursor=`) não compilar.

type NomesEm<R extends Rota, E extends ParametroEm> = R extends {
  readonly parametros: readonly (infer P)[];
}
  ? P extends { readonly em: E; readonly nome: infer N }
    ? N extends string
      ? N
      : never
    : never
  : never;

type ParametroEm = 'path' | 'query' | 'header';

type ValorDeConsulta = string | number | boolean | undefined;

/**
 * Vazio quando a rota não declara parâmetro daquele tipo.
 *
 * Caminho é OBRIGATÓRIO e consulta é OPCIONAL, e a diferença não é estética:
 * `{id}` sem valor não forma URL, enquanto `?cursor=` ausente é a primeira
 * página. Um helper só, exigindo os dois, obrigaria a escrever `consulta: {}`
 * em toda listagem.
 */
type SeTiverObrigatorio<Nomes extends string, Chave extends string, Valor> = [Nomes] extends [never]
  ? // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {}
  : { [K in Chave]: Valor };

type SeTiverOpcional<Nomes extends string, Chave extends string, Valor> = [Nomes] extends [never]
  ? // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {}
  : { [K in Chave]?: Valor };

/**
 * O corpo tipado pela schema — ou `FormData`, quando há arquivo (C27).
 *
 * ── O que a alternativa custa, e por que ainda vale ───────────────────────
 *
 * `FormData` é opaca: o TypeScript não confere campo nenhum dentro dela. A
 * garantia não some, mas MUDA DE LUGAR — o servidor analisa o multipart contra
 * a MESMA schema desta rota, em `EnviarMensagemSchema.safeParse`, e recusa com
 * `REQUISICAO_INVALIDA` apontando o campo.
 *
 * A troca é consciente: um envio com arquivo não tem como ser um objeto JSON,
 * e obrigar cada rota a declarar se aceita formulário acrescentaria uma
 * dimensão ao contrato inteiro para servir a uma rota. Quem usa `FormData` aqui
 * perde a conferência em tempo de compilação e continua tendo a do servidor.
 */
type CorpoDe<R extends Rota> = R extends { readonly corpo: infer S }
  ? S extends z.ZodType
    ? R extends { readonly corpoOpcional: true }
      ? { corpo?: z.input<S> | FormData }
      : { corpo: z.input<S> | FormData }
    : // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      {}
  : // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {};

export interface OpcoesComuns {
  /**
   * Cancelamento.
   *
   * A tela que troca de conversa antes de a anterior responder precisa disto:
   * sem cancelar, a resposta antiga chega depois da nova e sobrescreve a tela
   * com o conteúdo errado — a corrida que produz "cliquei na conversa A e
   * apareceu a B".
   */
  sinal?: AbortSignal;
  /** `Idempotency-Key` (B30). Obrigatório nos comandos que a rota exige. */
  idempotencyKey?: string;
}

export type Argumentos<R extends Rota> = OpcoesComuns &
  SeTiverObrigatorio<NomesEm<R, 'path'>, 'caminho', Record<NomesEm<R, 'path'>, string>> &
  SeTiverOpcional<
    NomesEm<R, 'query'>,
    'consulta',
    Partial<Record<NomesEm<R, 'query'>, ValorDeConsulta>>
  > &
  CorpoDe<R>;

// ── A chamada ─────────────────────────────────────────────────────────────

function montarCaminho(rota: Rota, caminho: Record<string, string> | undefined): string {
  return rota.caminho.replace(/\{(\w+)\}/g, (_todo, nome: string) => {
    const valor = caminho?.[nome];
    if (valor === undefined || valor === '') {
      // Sem isto, `{id}` ausente viraria a string literal `{id}` na URL e o
      // servidor responderia 404 — três camadas longe da causa real.
      throw new Error(`A rota ${rota.caminho} exige o parâmetro de caminho \`${nome}\`.`);
    }
    return encodeURIComponent(valor);
  });
}

function montarConsulta(consulta: Record<string, ValorDeConsulta> | undefined): string {
  if (!consulta) return '';
  const parametros = new URLSearchParams();
  for (const [nome, valor] of Object.entries(consulta)) {
    // `undefined` é ausência, não string vazia: `?cursor=undefined` viraria um
    // cursor inválido que o servidor recusa com 400.
    if (valor !== undefined) parametros.set(nome, String(valor));
  }
  const texto = parametros.toString();
  return texto ? `?${texto}` : '';
}

function correlacaoPadrao(): string {
  // `randomUUID` existe no navegador desde 2021 e no Node desde a 16. O ramo de
  // reserva cobre contexto não seguro (http em host que não é localhost), onde
  // ele não é exposto — e ali é melhor um id fraco que nenhum.
  return globalThis.crypto?.randomUUID?.() ?? `sem-crypto-${Date.now()}-${Math.random()}`;
}

export async function chamar<R extends Rota>(
  rota: R,
  opcoes: OpcoesDoCliente,
  argumentos?: Argumentos<Rota>,
): Promise<z.infer<R['resposta']>> {
  const buscar = opcoes.fetch ?? globalThis.fetch;
  const correlationId = (opcoes.novaCorrelacao ?? correlacaoPadrao)();
  const inicio = Date.now();

  const args = (argumentos ?? {}) as OpcoesComuns & {
    caminho?: Record<string, string>;
    consulta?: Record<string, ValorDeConsulta>;
    corpo?: unknown;
  };

  const caminho = montarCaminho(rota, args.caminho) + montarConsulta(args.consulta);
  const url = `${opcoes.baseUrl ?? ''}${caminho}`;
  const temCorpo = args.corpo !== undefined;
  /*
   * `FormData` passa inteiro, sem `JSON.stringify` e sem `content-type` (C27).
   *
   * O `fetch` escreve o cabeçalho sozinho, com o boundary que só ele conhece.
   * Declará-lo aqui produziria um `multipart/form-data` SEM boundary, e o
   * servidor receberia um corpo que não sabe separar — o erro que volta é de
   * campo ausente, e aponta para o lugar errado.
   *
   * Tudo o mais continua igual: autorização, correlação, idempotência e a
   * leitura da resposta não sabem que o corpo mudou de forma.
   */
  const ehFormulario = typeof FormData !== 'undefined' && args.corpo instanceof FormData;

  const registrar = (status: number | null, erro?: unknown): void => {
    opcoes.aoResponder?.({
      rota: rota.caminho,
      metodo: rota.metodo,
      caminho,
      status,
      correlationId,
      duracaoMs: Date.now() - inicio,
      ...(erro !== undefined ? { erro } : {}),
    });
  };

  let resposta: Response;
  try {
    resposta = await buscar(url, {
      method: rota.metodo,
      signal: args.sinal ?? null,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${await opcoes.token()}`,
        [CABECALHO_DE_CORRELACAO]: correlationId,
        ...(temCorpo && !ehFormulario ? { 'content-type': 'application/json' } : {}),
        ...(args.idempotencyKey ? { [CABECALHO_DE_IDEMPOTENCIA]: args.idempotencyKey } : {}),
      },
      ...(temCorpo
        ? { body: ehFormulario ? (args.corpo as FormData) : JSON.stringify(args.corpo) }
        : {}),
    });
  } catch (causa: unknown) {
    // Cancelamento não é falha: quem cancelou sabe por quê, e transformá-lo em
    // erro de rede faria a tela mostrar "sem conexão" ao trocar de conversa.
    if (causa instanceof Error && causa.name === 'AbortError') throw causa;
    registrar(null, causa);
    throw new ErroDeRede(rota.caminho, causa);
  }

  // O corpo pode não ser JSON, e o caso real é justamente o pior: proxy que
  // devolve `<html>502 Bad Gateway</html>`. Deixar o `JSON.parse` estourar
  // trocaria um `ErroDaApi` com status e `retryable` por um `SyntaxError` sem
  // nenhum dos dois — e a tela perderia como decidir se vale repetir.
  const texto = await resposta.text();
  let dados: unknown = null;
  let jsonValido = true;
  if (texto) {
    try {
      dados = JSON.parse(texto);
    } catch {
      jsonValido = false;
    }
  }

  if (!resposta.ok) {
    // Validar o corpo do erro também: servidor que devolve 500 com HTML de
    // proxy no meio produziria um `ErroDaApi` com campos indefinidos, e o
    // chamador decidiria com base em `undefined`.
    const analisado = ProblemaSchema.safeParse(dados);
    const erro = new ErroDaApi(
      resposta.status,
      analisado.success
        ? analisado.data
        : {
            type: 'about:blank',
            title: 'Resposta de erro fora do contrato',
            status: resposta.status,
            code: 'ERRO_INTERNO',
            // O do servidor ganha do nosso: é o que está no log dele.
            correlation_id: resposta.headers.get(CABECALHO_DE_CORRELACAO) ?? correlationId,
            retryable: resposta.status >= 500,
          },
      esperaPedida(resposta.headers.get('retry-after')),
    );
    registrar(resposta.status, erro);
    throw erro;
  }

  if (!jsonValido) {
    // Sucesso com corpo que não é JSON não pode passar como "resposta fora da
    // schema": o erro do zod falaria de campo faltando, e a causa é outra.
    const erro = new ErroDaApi(resposta.status, {
      type: 'about:blank',
      title: 'Resposta de sucesso que não é JSON',
      status: resposta.status,
      code: 'ERRO_INTERNO',
      correlation_id: resposta.headers.get(CABECALHO_DE_CORRELACAO) ?? correlationId,
      retryable: true,
    });
    registrar(resposta.status, erro);
    throw erro;
  }

  registrar(resposta.status);

  /*
   * 204 é "deu certo, e não há corpo" — é o que o HTTP diz, e o que as rotas
   * de exclusão respondem. O contrato delas é `{}`; validar o `null` do corpo
   * vazio contra `{}` recusava um sucesso, e a tela mostrava erro de algo que
   * já tinha acontecido (30/09/2026, excluir oportunidade e tarefa).
   */
  if (resposta.status === 204 && !texto) dados = {};

  // Validar a resposta, e não confiar nela: servidor mais novo pode ter mudado
  // o contrato, e falhar aqui aponta para o contrato — enquanto um `undefined`
  // vazando para a tela aponta para o componente errado.
  return rota.resposta.parse(dados) as z.infer<R['resposta']>;
}

/**
 * O cliente.
 *
 * Um método por rota, com nome de domínio. A alternativa — expor `chamar` e
 * deixar cada tela passar `ROTAS.x` — funcionaria, mas espalharia o
 * conhecimento de qual rota serve a qual caso de uso por quinze telas.
 *
 * `eventos` fica de fora: é `text/event-stream`, e um fluxo não cabe na forma
 * "requisição devolve corpo". Quem o consome é o leitor de SSE do `apps/web`,
 * que precisa de reconexão e de `Last-Event-ID` — coisas que não existem aqui.
 */
export function criarCliente(opcoes: OpcoesDoCliente) {
  return {
    /**
     * `GET /v1/entrada/organizacoes` — onde esta pessoa pode entrar.
     *
     * A única chamada que funciona com o token do PRIMEIRO passo do login, o
     * que prova quem a pessoa é e ainda não diz em qual revenda.
     */
    organizacoesDaPessoa: (args?: Argumentos<typeof ROTAS.organizacoesDaPessoa>) =>
      chamar(ROTAS.organizacoesDaPessoa, opcoes, args),

    /** `GET /v1/me` — sessão, tenant do vínculo, papel, escopo e flags. */
    me: (args?: Argumentos<typeof ROTAS.me>) => chamar(ROTAS.me, opcoes, args),

    /** `GET /v1/me/security` — a conta tem segundo fator? Vem do provedor. */
    segurancaDaConta: (args?: Argumentos<typeof ROTAS.segurancaDaConta>) =>
      chamar(ROTAS.segurancaDaConta, opcoes, args),

    // ── Atendimento ─────────────────────────────────────────────────────
    /** `GET /v1/inbox/conversations` — a fila de atendimento (D02). */
    listarInbox: (args?: Argumentos<typeof ROTAS.listarInbox>) =>
      chamar(ROTAS.listarInbox, opcoes, args),

    /** `GET /v1/conversations/{id}` — conversa composta com histórico (D01). */
    comporConversa: (args: Argumentos<typeof ROTAS.comporConversa>) =>
      chamar(ROTAS.comporConversa, opcoes, args),

    /** `POST /v1/conversations/{id}/messages` — envia, ou recusa com 503 (§14.3). */
    enviarMensagem: (args: Argumentos<typeof ROTAS.enviarMensagem>) =>
      chamar(ROTAS.enviarMensagem, opcoes, args),

    /** `POST /v1/conversations/{id}/resolve` — encerra ou reabre (D08, §10.4). */
    encerrarConversa: (args: Argumentos<typeof ROTAS.encerrarConversa>) =>
      chamar(ROTAS.encerrarConversa, opcoes, args),

    // ── Funil: as etapas ────────────────────────────────────────────────
    /** `GET /v1/funil/etapas` — as etapas, na ordem do quadro. */
    etapasDoFunil: (args?: Argumentos<typeof ROTAS.etapasDoFunil>) =>
      chamar(ROTAS.etapasDoFunil, opcoes, args),

    /** `POST /v1/funil/etapas` — acrescenta no fim. */
    criarEtapaDoFunil: (args: Argumentos<typeof ROTAS.criarEtapaDoFunil>) =>
      chamar(ROTAS.criarEtapaDoFunil, opcoes, args),

    /** `PATCH /v1/funil/etapas/{valor}` — rótulo e cor; o valor nunca muda. */
    renomearEtapaDoFunil: (args: Argumentos<typeof ROTAS.renomearEtapaDoFunil>) =>
      chamar(ROTAS.renomearEtapaDoFunil, opcoes, args),

    /** `PUT /v1/funil/etapas` — a ordem do quadro. */
    reordenarEtapasDoFunil: (args: Argumentos<typeof ROTAS.reordenarEtapasDoFunil>) =>
      chamar(ROTAS.reordenarEtapasDoFunil, opcoes, args),

    /** `DELETE /v1/funil/etapas/{valor}` — só etapa vazia. */
    removerEtapaDoFunil: (args: Argumentos<typeof ROTAS.removerEtapaDoFunil>) =>
      chamar(ROTAS.removerEtapaDoFunil, opcoes, args),

    /** `GET /v1/etiquetas` — o catálogo da conta, com as cores. */
    catalogoDeEtiquetas: (args?: Argumentos<typeof ROTAS.catalogoDeEtiquetas>) =>
      chamar(ROTAS.catalogoDeEtiquetas, opcoes, args),

    /** `POST /v1/etiquetas` — cria no catálogo. Idempotente por título. */
    criarEtiqueta: (args: Argumentos<typeof ROTAS.criarEtiqueta>) =>
      chamar(ROTAS.criarEtiqueta, opcoes, args),

    /** `PUT /v1/conversations/{id}/etiquetas` — substitui a lista inteira. */
    definirEtiquetasDaConversa: (args: Argumentos<typeof ROTAS.definirEtiquetasDaConversa>) =>
      chamar(ROTAS.definirEtiquetasDaConversa, opcoes, args),

    /** `POST /v1/conversations/{id}/lida` — o atendente viu esta conversa. */
    marcarConversaComoLida: (args: Argumentos<typeof ROTAS.marcarConversaComoLida>) =>
      chamar(ROTAS.marcarConversaComoLida, opcoes, args),

    /** `GET /v1/opportunities/{id}/responsaveis` — quem pode receber o card. */
    responsaveisDaOportunidade: (args: Argumentos<typeof ROTAS.responsaveisDaOportunidade>) =>
      chamar(ROTAS.responsaveisDaOportunidade, opcoes, args),

    /** `POST /v1/opportunities/{id}/responsavel` — passa o card e a conversa junto. */
    atribuirOportunidade: (args: Argumentos<typeof ROTAS.atribuirOportunidade>) =>
      chamar(ROTAS.atribuirOportunidade, opcoes, args),

    /** `POST /v1/conversations/{id}/prioridade` — troca o degrau; `null` limpa. */
    definirPrioridadeDaConversa: (args: Argumentos<typeof ROTAS.definirPrioridadeDaConversa>) =>
      chamar(ROTAS.definirPrioridadeDaConversa, opcoes, args),

    /** `GET /v1/conversations/{id}/responsaveis` — para quem ela pode ir. */
    responsaveisPossiveis: (args: Argumentos<typeof ROTAS.responsaveisPossiveis>) =>
      chamar(ROTAS.responsaveisPossiveis, opcoes, args),

    /** `POST /v1/conversations/{id}/responsavel` — passa conversa, cliente e oportunidade. */
    transferirConversa: (args: Argumentos<typeof ROTAS.transferirConversa>) =>
      chamar(ROTAS.transferirConversa, opcoes, args),

    // ── Cliente ─────────────────────────────────────────────────────────
    /** `GET /v1/customers/{id}/360` — a visão composta (D03). */
    /** A lista, que é o caminho até a ficha abaixo (D24). */
    listarClientes: (args?: Argumentos<typeof ROTAS.listarClientes>) =>
      chamar(ROTAS.listarClientes, opcoes, args),
    cliente360: (args: Argumentos<typeof ROTAS.clienteTrezentosESessenta>) =>
      chamar(ROTAS.clienteTrezentosESessenta, opcoes, args),
    /** `GET /v1/customers/{id}/cartao-de-contato` — o contato para compartilhar (#595). */
    cartaoDeContato: (args: Argumentos<typeof ROTAS.cartaoDeContato>) =>
      chamar(ROTAS.cartaoDeContato, opcoes, args),
    /** `DELETE /v1/opportunities/{id}` — exclui a oportunidade, sem volta. */
    removerOportunidade: (args: Argumentos<typeof ROTAS.removerOportunidade>) =>
      chamar(ROTAS.removerOportunidade, opcoes, args),

    /** `PATCH /v1/customers/{id}` — edita o cadastro: nome, e-mail, telefone e cargo (H08). */
    /**
     * `POST /v1/customers/{id}/iniciar-atendimento` — abre conversa por modelo.
     *
     * Para quem nunca escreveu: sem conversa não há onde enviar, e fora da
     * janela de 24 h só modelo aprovado sai.
     */
    iniciarAtendimento: (args: Argumentos<typeof ROTAS.iniciarAtendimento>) =>
      chamar(ROTAS.iniciarAtendimento, opcoes, args),

    atualizarCliente: (args: Argumentos<typeof ROTAS.atualizarCliente>) =>
      chamar(ROTAS.atualizarCliente, opcoes, args),

    /** `PATCH /v1/opportunities/{id}/notas/{notaId}` — corrige o texto; marca como editada. */
    editarNotaDaOportunidade: (args: Argumentos<typeof ROTAS.editarNotaDaOportunidade>) =>
      chamar(ROTAS.editarNotaDaOportunidade, opcoes, args),

    /** `GET /v1/mensagens-prontas` — os textos pré-escritos da organização. */
    mensagensProntas: (args: Argumentos<typeof ROTAS.mensagensProntas>) =>
      chamar(ROTAS.mensagensProntas, opcoes, args),

    criarMensagemPronta: (args: Argumentos<typeof ROTAS.criarMensagemPronta>) =>
      chamar(ROTAS.criarMensagemPronta, opcoes, args),

    editarMensagemPronta: (args: Argumentos<typeof ROTAS.editarMensagemPronta>) =>
      chamar(ROTAS.editarMensagemPronta, opcoes, args),

    removerMensagemPronta: (args: Argumentos<typeof ROTAS.removerMensagemPronta>) =>
      chamar(ROTAS.removerMensagemPronta, opcoes, args),

    /** `GET /v1/permissoes/catalogo` — o que se liga por pessoa (ADR-0034, #727). */
    catalogoDePermissoes: (args: Argumentos<typeof ROTAS.catalogoDePermissoes>) =>
      chamar(ROTAS.catalogoDePermissoes, opcoes, args),

    /** `GET /v1/opportunities/{id}/documentos` — os arquivos anexados. */
    documentosDaOportunidade: (args: Argumentos<typeof ROTAS.documentosDaOportunidade>) =>
      chamar(ROTAS.documentosDaOportunidade, opcoes, args),

    /** `POST /v1/opportunities/{id}/documentos` — `FormData` com o campo `arquivos`. */
    enviarDocumentosDaOportunidade: (
      args: Argumentos<typeof ROTAS.enviarDocumentosDaOportunidade>,
    ) => chamar(ROTAS.enviarDocumentosDaOportunidade, opcoes, args),

    removerDocumentoDaOportunidade: (
      args: Argumentos<typeof ROTAS.removerDocumentoDaOportunidade>,
    ) => chamar(ROTAS.removerDocumentoDaOportunidade, opcoes, args),

    /** `GET /v1/opportunities/{id}/conversas` — as conversas do cliente dela. */
    conversasDaOportunidade: (args: Argumentos<typeof ROTAS.conversasDaOportunidade>) =>
      chamar(ROTAS.conversasDaOportunidade, opcoes, args),

    /** `GET /v1/opportunities/{id}/historico` — o caminho dela pelo funil. */
    historicoDaOportunidade: (args: Argumentos<typeof ROTAS.historicoDaOportunidade>) =>
      chamar(ROTAS.historicoDaOportunidade, opcoes, args),
    atividadesDaOportunidade: (args: Argumentos<typeof ROTAS.atividadesDaOportunidade>) =>
      chamar(ROTAS.atividadesDaOportunidade, opcoes, args),

    /** `GET /v1/funil/motivos-de-perda` — o catálogo de motivos de perda. */
    motivosDePerda: (args: Argumentos<typeof ROTAS.motivosDePerda>) =>
      chamar(ROTAS.motivosDePerda, opcoes, args),

    criarMotivoDePerda: (args: Argumentos<typeof ROTAS.criarMotivoDePerda>) =>
      chamar(ROTAS.criarMotivoDePerda, opcoes, args),

    editarMotivoDePerda: (args: Argumentos<typeof ROTAS.editarMotivoDePerda>) =>
      chamar(ROTAS.editarMotivoDePerda, opcoes, args),

    /** `GET /v1/funil/roteamento` — para qual funil vai cada número ou canal. */
    roteamentoDeFunil: (args: Argumentos<typeof ROTAS.roteamentoDeFunil>) =>
      chamar(ROTAS.roteamentoDeFunil, opcoes, args),

    definirRoteamentoDeFunil: (args: Argumentos<typeof ROTAS.definirRoteamentoDeFunil>) =>
      chamar(ROTAS.definirRoteamentoDeFunil, opcoes, args),

    removerRoteamentoDeFunil: (args: Argumentos<typeof ROTAS.removerRoteamentoDeFunil>) =>
      chamar(ROTAS.removerRoteamentoDeFunil, opcoes, args),

    /** `POST /v1/funis` — cria o funil junto com a primeira etapa dele. */
    criarFunil: (args: Argumentos<typeof ROTAS.criarFunil>) =>
      chamar(ROTAS.criarFunil, opcoes, args),

    /** `PATCH /v1/funis/{chave}` — troca o nome; a chave é imutável. */
    renomearFunil: (args: Argumentos<typeof ROTAS.renomearFunil>) =>
      chamar(ROTAS.renomearFunil, opcoes, args),

    /** `GET /v1/funis` — os funis da organização, para a tela escolher. */
    funisDaOrganizacao: (args: Argumentos<typeof ROTAS.funisDaOrganizacao>) =>
      chamar(ROTAS.funisDaOrganizacao, opcoes, args),

    /** `GET /v1/inbox/nao-lidas` — o número do sino. */
    naoLidasDaInbox: (args: Argumentos<typeof ROTAS.naoLidasDaInbox>) =>
      chamar(ROTAS.naoLidasDaInbox, opcoes, args),
    /** `GET /v1/inbox/caixas` — as caixas do filtro da lista de conversas. */
    caixasDoAtendimento: (args?: Argumentos<typeof ROTAS.caixasDoAtendimento>) =>
      chamar(ROTAS.caixasDoAtendimento, opcoes, args),

    /** `GET /v1/notificacoes` — o conteúdo dos cards de mensagem recebida (#536). */
    notificacoes: (args: Argumentos<typeof ROTAS.notificacoes>) =>
      chamar(ROTAS.notificacoes, opcoes, args),

    /** `GET /v1/push/chave-publica` — a chave VAPID pública, ou `null` sem push configurado. */
    chavePublicaDoPush: (args: Argumentos<typeof ROTAS.chavePublicaDoPush>) =>
      chamar(ROTAS.chavePublicaDoPush, opcoes, args),

    /** `POST /v1/me/dispositivos` — guarda a inscrição de push deste navegador. */
    registrarDispositivo: (args: Argumentos<typeof ROTAS.registrarDispositivo>) =>
      chamar(ROTAS.registrarDispositivo, opcoes, args),

    /** `POST /v1/me/dispositivos/remover` — deixa de receber push neste navegador. */
    removerDispositivo: (args: Argumentos<typeof ROTAS.removerDispositivo>) =>
      chamar(ROTAS.removerDispositivo, opcoes, args),

    /** `POST /v1/notificacoes/vistas` — o card saiu da tela, e não volta em nenhuma aba. */
    marcarNotificacoesVistas: (args: Argumentos<typeof ROTAS.marcarNotificacoesVistas>) =>
      chamar(ROTAS.marcarNotificacoesVistas, opcoes, args),

    /** `GET /v1/opportunities/{id}` — uma oportunidade, com o card do quadro. */
    lerOportunidade: (args: Argumentos<typeof ROTAS.lerOportunidade>) =>
      chamar(ROTAS.lerOportunidade, opcoes, args),

    /** `GET /v1/opportunities/{id}/notas` — o que foi combinado na negociação. */
    notasDaOportunidade: (args: Argumentos<typeof ROTAS.notasDaOportunidade>) =>
      chamar(ROTAS.notasDaOportunidade, opcoes, args),

    /** `POST /v1/opportunities/{id}/notas` — acrescenta; não existe editar nem apagar. */
    criarNotaDaOportunidade: (args: Argumentos<typeof ROTAS.criarNotaDaOportunidade>) =>
      chamar(ROTAS.criarNotaDaOportunidade, opcoes, args),

    /** `PUT /v1/me/foto` — troca a própria foto; o corpo é `FormData` com `foto`. */
    definirFotoDePerfil: (args: Argumentos<typeof ROTAS.definirFotoDePerfil>) =>
      chamar(ROTAS.definirFotoDePerfil, opcoes, args),

    /** `DELETE /v1/me/foto` — tira a própria foto. */
    removerFotoDePerfil: (args: Argumentos<typeof ROTAS.removerFotoDePerfil> = {}) =>
      chamar(ROTAS.removerFotoDePerfil, opcoes, args),

    /** `POST /v1/feedbacks` — o feedback ao suporte, com imagens (#561). */
    enviarFeedback: (args: Argumentos<typeof ROTAS.enviarFeedback>) =>
      chamar(ROTAS.enviarFeedback, opcoes, args),
    /** `GET /v1/feedbacks` — a lista do suporte (#561). */
    listarFeedbacks: (args: Argumentos<typeof ROTAS.listarFeedbacks> = {}) =>
      chamar(ROTAS.listarFeedbacks, opcoes, args),

    /** `GET /v1/customers/{id}/notas` — o que a equipe escreveu sobre o cliente. */
    notasDoCliente: (args: Argumentos<typeof ROTAS.notasDoCliente>) =>
      chamar(ROTAS.notasDoCliente, opcoes, args),

    /** `POST /v1/customers/{id}/notas` — escreve uma nota sobre o cliente. */
    criarNotaDoCliente: (args: Argumentos<typeof ROTAS.criarNotaDoCliente>) =>
      chamar(ROTAS.criarNotaDoCliente, opcoes, args),
    fichaDoPaciente: (args: Argumentos<typeof ROTAS.fichaDoPaciente>) =>
      chamar(ROTAS.fichaDoPaciente, opcoes, args),
    salvarFichaDoPaciente: (args: Argumentos<typeof ROTAS.salvarFichaDoPaciente>) =>
      chamar(ROTAS.salvarFichaDoPaciente, opcoes, args),
    consultaDaOportunidade: (args: Argumentos<typeof ROTAS.consultaDaOportunidade>) =>
      chamar(ROTAS.consultaDaOportunidade, opcoes, args),
    salvarConsultaDaOportunidade: (args: Argumentos<typeof ROTAS.salvarConsultaDaOportunidade>) =>
      chamar(ROTAS.salvarConsultaDaOportunidade, opcoes, args),
    opcoesDaConsulta: (args: Argumentos<typeof ROTAS.opcoesDaConsulta> = {}) =>
      chamar(ROTAS.opcoesDaConsulta, opcoes, args),
    sincronizarOpcoesDaConsulta: (args: Argumentos<typeof ROTAS.sincronizarOpcoesDaConsulta>) =>
      chamar(ROTAS.sincronizarOpcoesDaConsulta, opcoes, args),
    listarPrecosDaClinica: (args: Argumentos<typeof ROTAS.listarPrecosDaClinica> = {}) =>
      chamar(ROTAS.listarPrecosDaClinica, opcoes, args),
    precoDaConsultaDoMedico: (args: Argumentos<typeof ROTAS.precoDaConsultaDoMedico>) =>
      chamar(ROTAS.precoDaConsultaDoMedico, opcoes, args),
    sincronizarPrecosDaClinica: (args: Argumentos<typeof ROTAS.sincronizarPrecosDaClinica> = {}) =>
      chamar(ROTAS.sincronizarPrecosDaClinica, opcoes, args),
    agendarConsulta: (args: Argumentos<typeof ROTAS.agendarConsulta>) =>
      chamar(ROTAS.agendarConsulta, opcoes, args),
    cancelarConsulta: (args: Argumentos<typeof ROTAS.cancelarConsulta>) =>
      chamar(ROTAS.cancelarConsulta, opcoes, args),
    /** `POST /v1/pipelines/{id}/atualizar` — o "Atualizar" do Funil (trava de 2 min). */
    atualizarFunil: (args: Argumentos<typeof ROTAS.atualizarFunil>) =>
      chamar(ROTAS.atualizarFunil, opcoes, args),
    configuracaoDoAtualizar: (args: Argumentos<typeof ROTAS.configuracaoDoAtualizar>) =>
      chamar(ROTAS.configuracaoDoAtualizar, opcoes, args),
    definirConfiguracaoDoAtualizar: (
      args: Argumentos<typeof ROTAS.definirConfiguracaoDoAtualizar>,
    ) => chamar(ROTAS.definirConfiguracaoDoAtualizar, opcoes, args),
    agendaDaClinica: (args: Argumentos<typeof ROTAS.agendaDaClinica> = {}) =>
      chamar(ROTAS.agendaDaClinica, opcoes, args),
    /** `GET /v1/opportunities/{id}/consulta/agendamento` — o agendamento gravado na vaga (#1187). */
    agendamentoDaConsulta: (args: Argumentos<typeof ROTAS.agendamentoDaConsulta>) =>
      chamar(ROTAS.agendamentoDaConsulta, opcoes, args),
    /** `POST /v1/opportunities/{id}/consulta/remarcar` — atualiza a consulta marcada (#1187). */
    remarcarConsulta: (args: Argumentos<typeof ROTAS.remarcarConsulta>) =>
      chamar(ROTAS.remarcarConsulta, opcoes, args),
    gravarAgendaDaClinica: (args: Argumentos<typeof ROTAS.gravarAgendaDaClinica>) =>
      chamar(ROTAS.gravarAgendaDaClinica, opcoes, args),
    /** `POST /v1/admin/clinica/agenda/lista` — uma lista da clínica, para os padrões (#1187). */
    listaDaAgendaDaClinica: (args: Argumentos<typeof ROTAS.listaDaAgendaDaClinica>) =>
      chamar(ROTAS.listaDaAgendaDaClinica, opcoes, args),
    pacienteVinculadoDaConsulta: (args: Argumentos<typeof ROTAS.pacienteVinculadoDaConsulta>) =>
      chamar(ROTAS.pacienteVinculadoDaConsulta, opcoes, args),
    cadastrarPacienteNaAgenda: (args: Argumentos<typeof ROTAS.cadastrarPacienteNaAgenda>) =>
      chamar(ROTAS.cadastrarPacienteNaAgenda, opcoes, args),
    pacienteDoCliente: (args: Argumentos<typeof ROTAS.pacienteDoCliente>) =>
      chamar(ROTAS.pacienteDoCliente, opcoes, args),
    buscarPacienteDoCliente: (args: Argumentos<typeof ROTAS.buscarPacienteDoCliente>) =>
      chamar(ROTAS.buscarPacienteDoCliente, opcoes, args),
    ultimasVezesDoPaciente: (args: Argumentos<typeof ROTAS.ultimasVezesDoPaciente>) =>
      chamar(ROTAS.ultimasVezesDoPaciente, opcoes, args),
    vincularPacienteDoCliente: (args: Argumentos<typeof ROTAS.vincularPacienteDoCliente>) =>
      chamar(ROTAS.vincularPacienteDoCliente, opcoes, args),
    desvincularPacienteDoCliente: (args: Argumentos<typeof ROTAS.desvincularPacienteDoCliente>) =>
      chamar(ROTAS.desvincularPacienteDoCliente, opcoes, args),
    cadastrarPacienteDoCliente: (args: Argumentos<typeof ROTAS.cadastrarPacienteDoCliente>) =>
      chamar(ROTAS.cadastrarPacienteDoCliente, opcoes, args),
    consultarSisac: (args: Argumentos<typeof ROTAS.consultarSisac>) =>
      chamar(ROTAS.consultarSisac, opcoes, args),
    documentosDoPaciente: (args: Argumentos<typeof ROTAS.documentosDoPaciente>) =>
      chamar(ROTAS.documentosDoPaciente, opcoes, args),
    enviarDocumentosDoPaciente: (args: Argumentos<typeof ROTAS.enviarDocumentosDoPaciente>) =>
      chamar(ROTAS.enviarDocumentosDoPaciente, opcoes, args),
    removerDocumentoDoPaciente: (args: Argumentos<typeof ROTAS.removerDocumentoDoPaciente>) =>
      chamar(ROTAS.removerDocumentoDoPaciente, opcoes, args),
    conveniosDoPaciente: (args: Argumentos<typeof ROTAS.conveniosDoPaciente>) =>
      chamar(ROTAS.conveniosDoPaciente, opcoes, args),
    criarConvenioDoPaciente: (args: Argumentos<typeof ROTAS.criarConvenioDoPaciente>) =>
      chamar(ROTAS.criarConvenioDoPaciente, opcoes, args),
    editarConvenioDoPaciente: (args: Argumentos<typeof ROTAS.editarConvenioDoPaciente>) =>
      chamar(ROTAS.editarConvenioDoPaciente, opcoes, args),
    removerConvenioDoPaciente: (args: Argumentos<typeof ROTAS.removerConvenioDoPaciente>) =>
      chamar(ROTAS.removerConvenioDoPaciente, opcoes, args),
    operadoras: (args: Argumentos<typeof ROTAS.operadoras> = {}) =>
      chamar(ROTAS.operadoras, opcoes, args),
    criarOperadora: (args: Argumentos<typeof ROTAS.criarOperadora>) =>
      chamar(ROTAS.criarOperadora, opcoes, args),
    editarOperadora: (args: Argumentos<typeof ROTAS.editarOperadora>) =>
      chamar(ROTAS.editarOperadora, opcoes, args),
    examesDoPaciente: (args: Argumentos<typeof ROTAS.examesDoPaciente>) =>
      chamar(ROTAS.examesDoPaciente, opcoes, args),
    criarExameDoPaciente: (args: Argumentos<typeof ROTAS.criarExameDoPaciente>) =>
      chamar(ROTAS.criarExameDoPaciente, opcoes, args),
    editarExameDoPaciente: (args: Argumentos<typeof ROTAS.editarExameDoPaciente>) =>
      chamar(ROTAS.editarExameDoPaciente, opcoes, args),
    definirEtapasDoExame: (args: Argumentos<typeof ROTAS.definirEtapasDoExame>) =>
      chamar(ROTAS.definirEtapasDoExame, opcoes, args),
    concluirEtapaDoExame: (args: Argumentos<typeof ROTAS.concluirEtapaDoExame>) =>
      chamar(ROTAS.concluirEtapaDoExame, opcoes, args),
    desfazerEtapaDoExame: (args: Argumentos<typeof ROTAS.desfazerEtapaDoExame>) =>
      chamar(ROTAS.desfazerEtapaDoExame, opcoes, args),
    cancelarExameDoPaciente: (args: Argumentos<typeof ROTAS.cancelarExameDoPaciente>) =>
      chamar(ROTAS.cancelarExameDoPaciente, opcoes, args),
    etapasDaClinica: (args: Argumentos<typeof ROTAS.etapasDaClinica> = {}) =>
      chamar(ROTAS.etapasDaClinica, opcoes, args),
    criarEtapaDaClinica: (args: Argumentos<typeof ROTAS.criarEtapaDaClinica>) =>
      chamar(ROTAS.criarEtapaDaClinica, opcoes, args),
    editarEtapaDaClinica: (args: Argumentos<typeof ROTAS.editarEtapaDaClinica>) =>
      chamar(ROTAS.editarEtapaDaClinica, opcoes, args),
    ordenarEtapasDaClinica: (args: Argumentos<typeof ROTAS.ordenarEtapasDaClinica>) =>
      chamar(ROTAS.ordenarEtapasDaClinica, opcoes, args),
    camposPersonalizados: (args: Argumentos<typeof ROTAS.camposPersonalizados>) =>
      chamar(ROTAS.camposPersonalizados, opcoes, args),
    criarCampoPersonalizado: (args: Argumentos<typeof ROTAS.criarCampoPersonalizado>) =>
      chamar(ROTAS.criarCampoPersonalizado, opcoes, args),
    editarCampoPersonalizado: (args: Argumentos<typeof ROTAS.editarCampoPersonalizado>) =>
      chamar(ROTAS.editarCampoPersonalizado, opcoes, args),
    ordenarCamposPersonalizados: (args: Argumentos<typeof ROTAS.ordenarCamposPersonalizados>) =>
      chamar(ROTAS.ordenarCamposPersonalizados, opcoes, args),
    tratamentosDoPaciente: (args: Argumentos<typeof ROTAS.tratamentosDoPaciente>) =>
      chamar(ROTAS.tratamentosDoPaciente, opcoes, args),
    criarTratamentoDoPaciente: (args: Argumentos<typeof ROTAS.criarTratamentoDoPaciente>) =>
      chamar(ROTAS.criarTratamentoDoPaciente, opcoes, args),
    editarTratamentoDoPaciente: (args: Argumentos<typeof ROTAS.editarTratamentoDoPaciente>) =>
      chamar(ROTAS.editarTratamentoDoPaciente, opcoes, args),
    ajustarValorDoTratamento: (args: Argumentos<typeof ROTAS.ajustarValorDoTratamento>) =>
      chamar(ROTAS.ajustarValorDoTratamento, opcoes, args),
    catalogoDeTratamentos: (args: Argumentos<typeof ROTAS.catalogoDeTratamentos> = {}) =>
      chamar(ROTAS.catalogoDeTratamentos, opcoes, args),
    prontuarioDoPaciente: (args: Argumentos<typeof ROTAS.prontuarioDoPaciente>) =>
      chamar(ROTAS.prontuarioDoPaciente, opcoes, args),
    definirAcessoAoProntuario: (args: Argumentos<typeof ROTAS.definirAcessoAoProntuario>) =>
      chamar(ROTAS.definirAcessoAoProntuario, opcoes, args),

    /** `PATCH /v1/customers/{id}/notas/{notaId}` — corrige; a nota fica marcada. */
    editarNotaDoCliente: (args: Argumentos<typeof ROTAS.editarNotaDoCliente>) =>
      chamar(ROTAS.editarNotaDoCliente, opcoes, args),

    /** `DELETE /v1/customers/{id}/notas/{notaId}` — exclui. */
    removerNotaDoCliente: (args: Argumentos<typeof ROTAS.removerNotaDoCliente>) =>
      chamar(ROTAS.removerNotaDoCliente, opcoes, args),

    /** `PUT /v1/customers/{id}/etiquetas` — as do CLIENTE, não as das conversas dele. */
    definirEtiquetasDoCliente: (args: Argumentos<typeof ROTAS.definirEtiquetasDoCliente>) =>
      chamar(ROTAS.definirEtiquetasDoCliente, opcoes, args),

    /** `POST /v1/customers` — cadastra à mão quem chegou antes de existir conversa. */
    criarCliente: (args: Argumentos<typeof ROTAS.criarCliente>) =>
      chamar(ROTAS.criarCliente, opcoes, args),

    /** `GET /v1/customers/export` — a lista com o contato inteiro, e o acesso registrado. */
    exportarClientes: (args?: Argumentos<typeof ROTAS.exportarClientes>) =>
      chamar(ROTAS.exportarClientes, opcoes, args),

    /** `GET /v1/timeline` — a linha do tempo materializada (D06). */
    listarTimeline: (args?: Argumentos<typeof ROTAS.listarTimeline>) =>
      chamar(ROTAS.listarTimeline, opcoes, args),

    // ── Funil ───────────────────────────────────────────────────────────
    /** `GET /v1/pipelines/{id}/board` — as colunas e os cartões (D04). */
    boardDoFunil: (args: Argumentos<typeof ROTAS.boardDoFunil>) =>
      chamar(ROTAS.boardDoFunil, opcoes, args),
    atendimentosDoFunil: (args: Argumentos<typeof ROTAS.atendimentosDoFunil>) =>
      chamar(ROTAS.atendimentosDoFunil, opcoes, args),
    atendimentosDaOportunidade: (args: Argumentos<typeof ROTAS.atendimentosDaOportunidade>) =>
      chamar(ROTAS.atendimentosDaOportunidade, opcoes, args),

    /** `POST /v1/opportunities` — nasce no cadastro comercial (#699). */
    criarOportunidade: (args: Argumentos<typeof ROTAS.criarOportunidade>) =>
      chamar(ROTAS.criarOportunidade, opcoes, args),

    /** `PATCH /v1/opportunities/{id}` — move de etapa, com controle de versão. */
    moverEtapa: (args: Argumentos<typeof ROTAS.moverEtapa>) =>
      chamar(ROTAS.moverEtapa, opcoes, args),

    /*
     * O MESMO PATCH, com outro nome.
     *
     * Mover e editar são a mesma rota — um recurso, um PATCH —, e o corpo
     * distingue. Mas a tela que abre o formulário de edição chamando
     * `moverEtapa` leria como defeito, e quem for consertar o formulário
     * procuraria no lugar errado. O apelido custa duas linhas e diz a verdade
     * no ponto de uso.
     */
    editarOportunidade: (args: Argumentos<typeof ROTAS.moverEtapa>) =>
      chamar(ROTAS.moverEtapa, opcoes, args),

    // ── Tarefas ─────────────────────────────────────────────────────────
    /** `GET /v1/tasks` — as tarefas do escopo (D05). */
    listarTarefas: (args?: Argumentos<typeof ROTAS.listarTarefas>) =>
      chamar(ROTAS.listarTarefas, opcoes, args),

    /** `POST /v1/tasks` — nasce no cadastro comercial, com a origem daqui. */
    criarTarefa: (args: Argumentos<typeof ROTAS.criarTarefa>) =>
      chamar(ROTAS.criarTarefa, opcoes, args),

    /** `GET /v1/tasks/responsaveis` — a quem se pode atribuir, na carteira de quem pede. */
    listarResponsaveisDeTarefa: (args?: Argumentos<typeof ROTAS.listarResponsaveisDeTarefa>) =>
      chamar(ROTAS.listarResponsaveisDeTarefa, opcoes, args),

    /** `GET /v1/tasks/{id}` — a tarefa inteira, com o corpo (da port, não da projeção). */
    lerTarefa: (args: Argumentos<typeof ROTAS.lerTarefa>) => chamar(ROTAS.lerTarefa, opcoes, args),

    /** `PATCH /v1/tasks/{id}` — campo ausente não é tocado; `null` limpa. */
    alterarTarefa: (args: Argumentos<typeof ROTAS.alterarTarefa>) =>
      chamar(ROTAS.alterarTarefa, opcoes, args),

    /** `DELETE /v1/tasks/{id}` — apaga de verdade, e é auditado. */
    removerTarefa: (args: Argumentos<typeof ROTAS.removerTarefa>) =>
      chamar(ROTAS.removerTarefa, opcoes, args),

    /** `POST /v1/tasks/{id}/complete` — idempotente. */
    concluirTarefa: (args: Argumentos<typeof ROTAS.concluirTarefa>) =>
      chamar(ROTAS.concluirTarefa, opcoes, args),

    // ── Configuração (D13) ──────────────────────────────────────────────
    /** `GET /v1/admin/settings` — a configuração da revenda. */
    lerConfiguracao: (args?: Argumentos<typeof ROTAS.lerConfiguracao>) =>
      chamar(ROTAS.lerConfiguracao, opcoes, args),

    /** `PATCH /v1/admin/settings` — altera com justificativa. */
    alterarConfiguracao: (args: Argumentos<typeof ROTAS.alterarConfiguracao>) =>
      chamar(ROTAS.alterarConfiguracao, opcoes, args),

    /** `POST /v1/admin/settings/simulate` — roda a regra antes de salvar. */
    simularRegraDeOportunidade: (args: Argumentos<typeof ROTAS.simularRegraDeOportunidade>) =>
      chamar(ROTAS.simularRegraDeOportunidade, opcoes, args),

    /** `GET /v1/admin/flags` — o catálogo, com o que está ligado. */
    listarFlags: (args?: Argumentos<typeof ROTAS.listarFlags>) =>
      chamar(ROTAS.listarFlags, opcoes, args),

    /** `PATCH /v1/admin/flags/{chave}` — liga (homologando) ou desliga. */
    alterarFlag: (args: Argumentos<typeof ROTAS.alterarFlag>) =>
      chamar(ROTAS.alterarFlag, opcoes, args),

    // ── Administração de pessoas, times e unidades (D12) ────────────────
    /** `GET /v1/admin/unidades` — inclui as inativas, com o status. */
    listarUnidades: (args?: Argumentos<typeof ROTAS.listarUnidades>) =>
      chamar(ROTAS.listarUnidades, opcoes, args),

    /** `POST /v1/admin/unidades` — nome repetido na revenda dá 409. */
    criarUnidade: (args: Argumentos<typeof ROTAS.criarUnidade>) =>
      chamar(ROTAS.criarUnidade, opcoes, args),

    /** `PATCH /v1/admin/unidades/{id}` — renomeia ou desativa; não apaga. */
    alterarUnidade: (args: Argumentos<typeof ROTAS.alterarUnidade>) =>
      chamar(ROTAS.alterarUnidade, opcoes, args),

    /** `GET /v1/admin/times` — cada um com a unidade, quando tem. */
    listarTimes: (args?: Argumentos<typeof ROTAS.listarTimes>) =>
      chamar(ROTAS.listarTimes, opcoes, args),

    /** `POST /v1/admin/times` — unidade é opcional. */
    criarTime: (args: Argumentos<typeof ROTAS.criarTime>) => chamar(ROTAS.criarTime, opcoes, args),

    /** `PATCH /v1/admin/times/{id}` — `unidadeId: null` desvincula. */
    alterarTime: (args: Argumentos<typeof ROTAS.alterarTime>) =>
      chamar(ROTAS.alterarTime, opcoes, args),

    /** `GET /v1/admin/pessoas` — inclui quem está provisionando e quem falhou. */
    listarPessoas: (args?: Argumentos<typeof ROTAS.listarPessoas>) =>
      chamar(ROTAS.listarPessoas, opcoes, args),

    /** `POST /v1/admin/pessoas` — 202: a saga (B28) conclui depois. */
    convidarPessoa: (args: Argumentos<typeof ROTAS.convidarPessoa>) =>
      chamar(ROTAS.convidarPessoa, opcoes, args),

    /** `PATCH /v1/admin/pessoas/{id}` — papel, escopo e lotação. */
    alterarPessoa: (args: Argumentos<typeof ROTAS.alterarPessoa>) =>
      chamar(ROTAS.alterarPessoa, opcoes, args),

    /** `POST /v1/admin/pessoas/{id}/desativar` — a resposta diz engine por engine. */
    desativarPessoa: (args: Argumentos<typeof ROTAS.desativarPessoa>) =>
      chamar(ROTAS.desativarPessoa, opcoes, args),

    /**
     * `POST /v1/admin/pessoas/{id}/reativar` — o contrário exato; no limite de
     * usuários, o corpo traz a confirmação de contratar (#768).
     */
    reativarPessoa: (args: Argumentos<typeof ROTAS.reativarPessoa>) =>
      chamar(ROTAS.reativarPessoa, opcoes, args),
    reprovisionarPessoa: (args: Argumentos<typeof ROTAS.reprovisionarPessoa>) =>
      chamar(ROTAS.reprovisionarPessoa, opcoes, args),

    /** `GET /v1/admin/limites` — as vagas do contrato, para a tela de Pessoas (#768). */
    limitesDaOrganizacao: (args?: Argumentos<typeof ROTAS.limitesDaOrganizacao>) =>
      chamar(ROTAS.limitesDaOrganizacao, opcoes, args),

    /**
     * `POST /v1/admin/armazenamento/contratar` — mais um bloco de armazenamento
     * (#718), confirmando o valor do bloco; devolve os limites já com ele.
     */
    contratarArmazenamento: (args: Argumentos<typeof ROTAS.contratarArmazenamento>) =>
      chamar(ROTAS.contratarArmazenamento, opcoes, args),

    /** `POST /v1/admin/pessoas/{id}/redefinir-senha` — a senha provisória sai só aqui. */
    redefinirSenhaDaPessoa: (args: Argumentos<typeof ROTAS.redefinirSenhaDaPessoa>) =>
      chamar(ROTAS.redefinirSenhaDaPessoa, opcoes, args),

    /** `POST /v1/admin/pessoas/redistribuir` — negócios em aberto, em lote. */
    /** `GET /v1/admin/pessoas/{id}/carteira` — quantos negócios em aberto ela tem. */
    carteiraDaPessoa: (args: Argumentos<typeof ROTAS.carteiraDaPessoa>) =>
      chamar(ROTAS.carteiraDaPessoa, opcoes, args),

    redistribuirCarteira: (args: Argumentos<typeof ROTAS.redistribuirCarteira>) =>
      chamar(ROTAS.redistribuirCarteira, opcoes, args),

    // ── Identidade (D09) ────────────────────────────────────────────────
    /** `GET /v1/identity/merge-candidates` — a fila do degrau 6 (§5.2). */
    listarCandidatosDeMerge: (args?: Argumentos<typeof ROTAS.listarCandidatosDeMerge>) =>
      chamar(ROTAS.listarCandidatosDeMerge, opcoes, args),

    /** `GET /v1/identity/merge-candidates/{id}` — comparação lado a lado. */
    detalharCandidatoDeMerge: (args: Argumentos<typeof ROTAS.detalharCandidatoDeMerge>) =>
      chamar(ROTAS.detalharCandidatoDeMerge, opcoes, args),

    /** `POST .../approve` — sobrevivente explícito, sem padrão. */
    aprovarCandidatoDeMerge: (args: Argumentos<typeof ROTAS.aprovarCandidatoDeMerge>) =>
      chamar(ROTAS.aprovarCandidatoDeMerge, opcoes, args),

    /** `POST .../reject` — motivo obrigatório. */
    rejeitarCandidatoDeMerge: (args: Argumentos<typeof ROTAS.rejeitarCandidatoDeMerge>) =>
      chamar(ROTAS.rejeitarCandidatoDeMerge, opcoes, args),

    /** `GET /v1/identity/metrics` — a taxa de vínculo automático (§14.2). */
    medirIdentidade: (args?: Argumentos<typeof ROTAS.medirIdentidade>) =>
      chamar(ROTAS.medirIdentidade, opcoes, args),

    // ── Indicadores ─────────────────────────────────────────────────────
    /** `GET /v1/dashboard` — os indicadores servidos de agregado (D07). */
    lerDashboard: (args?: Argumentos<typeof ROTAS.lerDashboard>) =>
      chamar(ROTAS.lerDashboard, opcoes, args),

    /** `GET /v1/relatorios/ranking-de-vendas` — quem vendeu mais no período (#557). */
    lerRankingDeVendas: (args?: Argumentos<typeof ROTAS.lerRankingDeVendas>) =>
      chamar(ROTAS.lerRankingDeVendas, opcoes, args),

    /** `GET /v1/relatorios/ranking-de-vendas/vendas` — as vendas de uma pessoa do ranking. */
    lerVendasDoRanking: (args: Argumentos<typeof ROTAS.lerVendasDoRanking>) =>
      chamar(ROTAS.lerVendasDoRanking, opcoes, args),

    /** `GET /v1/relatorios/comparativos` — o período contra a base escolhida (#557). */
    lerComparativos: (args?: Argumentos<typeof ROTAS.lerComparativos>) =>
      chamar(ROTAS.lerComparativos, opcoes, args),

    /** `GET /v1/relatorios/gargalos` — onde as oportunidades param no funil (#557). */
    lerGargalosDoFunil: (args?: Argumentos<typeof ROTAS.lerGargalosDoFunil>) =>
      chamar(ROTAS.lerGargalosDoFunil, opcoes, args),

    /** `GET /v1/relatorios/gargalos/paradas` — as oportunidades paradas de um número. */
    lerOportunidadesParadas: (args?: Argumentos<typeof ROTAS.lerOportunidadesParadas>) =>
      chamar(ROTAS.lerOportunidadesParadas, opcoes, args),

    /** `GET /v1/relatorios/atendimento` — volume por canal, por hora e por pessoa (#557). */
    lerAtendimentoDoPeriodo: (args?: Argumentos<typeof ROTAS.lerAtendimentoDoPeriodo>) =>
      chamar(ROTAS.lerAtendimentoDoPeriodo, opcoes, args),

    // ── Segurança ───────────────────────────────────────────────────────
    /** `GET /v1/security/mfa` — a política do tenant (B15). */
    lerPoliticaDeMfa: (args?: Argumentos<typeof ROTAS.lerPoliticaDeMfa>) =>
      chamar(ROTAS.lerPoliticaDeMfa, opcoes, args),

    /** `PATCH /v1/security/mfa` — liga ou desliga a exigência, com justificativa. */
    definirPoliticaDeMfa: (args: Argumentos<typeof ROTAS.definirPoliticaDeMfa>) =>
      chamar(ROTAS.definirPoliticaDeMfa, opcoes, args),

    /** `POST /v1/users/{id}/mfa/reset` — reset com justificativa auditada. */
    resetarSegundoFator: (args: Argumentos<typeof ROTAS.resetarSegundoFator>) =>
      chamar(ROTAS.resetarSegundoFator, opcoes, args),

    /**
     * `POST /v1/step-up` — acesso elevado temporário (B17).
     *
     * Chamar isto exige um token RECÉM-obtido: o servidor confere o `auth_time`.
     * A tela precisa mandar a pessoa reautenticar antes, e não depois de tomar
     * o 403 — a extensão `step_up` do erro traz o que dizer a ela.
     */
    pedirStepUp: (args: Argumentos<typeof ROTAS.pedirStepUp>) =>
      chamar(ROTAS.pedirStepUp, opcoes, args),

    /** `GET /v1/admin/dlq` — fila de descarte, do mais velho para o mais novo (D10). */
    listarDlq: (args: Argumentos<typeof ROTAS.listarDlq>) => chamar(ROTAS.listarDlq, opcoes, args),

    /** `GET /v1/admin/dlq/{id}` — payload referenciado e histórico de tentativas. */
    detalharItemDaDlq: (args: Argumentos<typeof ROTAS.detalharItemDaDlq>) =>
      chamar(ROTAS.detalharItemDaDlq, opcoes, args),

    /** `POST /v1/admin/dlq/reprocess` — simula por padrão; `simular: false` executa. */
    reprocessarDaDlq: (args: Argumentos<typeof ROTAS.reprocessarDaDlq>) =>
      chamar(ROTAS.reprocessarDaDlq, opcoes, args),

    /** `POST /v1/admin/dlq/{id}/resolve` — sem reprocessar, com motivo obrigatório. */
    resolverItemDaDlq: (args: Argumentos<typeof ROTAS.resolverItemDaDlq>) =>
      chamar(ROTAS.resolverItemDaDlq, opcoes, args),

    /** `GET .../channels/whatsapp/{id}/modelos` — lidos da Meta, com status e motivo da recusa. */
    modelosDeMensagem: (args: Argumentos<typeof ROTAS.modelosDeMensagem>) =>
      chamar(ROTAS.modelosDeMensagem, opcoes, args),

    /** `POST .../channels/whatsapp/{id}/modelos` — cadastra para aprovação da Meta. */
    criarModeloDeMensagem: (args: Argumentos<typeof ROTAS.criarModeloDeMensagem>) =>
      chamar(ROTAS.criarModeloDeMensagem, opcoes, args),

    /** `DELETE .../channels/whatsapp/{id}/modelos/{nome}` — apaga na Meta. */
    /** `POST …/whatsapp/{id}/modelos/sincronizar` — pede ao motor que releia a Meta. */
    sincronizarModelosDeMensagem: (args: Argumentos<typeof ROTAS.sincronizarModelosDeMensagem>) =>
      chamar(ROTAS.sincronizarModelosDeMensagem, opcoes, args),

    removerModeloDeMensagem: (args: Argumentos<typeof ROTAS.removerModeloDeMensagem>) =>
      chamar(ROTAS.removerModeloDeMensagem, opcoes, args),

    /** `PATCH .../channels/whatsapp/{id}/mensagens-automaticas` — saudação e fora de expediente. */
    alterarMensagensAutomaticas: (args: Argumentos<typeof ROTAS.alterarMensagensAutomaticas>) =>
      chamar(ROTAS.alterarMensagensAutomaticas, opcoes, args),

    /** `GET /v1/admin/integrations` — conexões de engine, sem segredo (D11). */
    listarIntegracoes: (args: Argumentos<typeof ROTAS.listarIntegracoes>) =>
      chamar(ROTAS.listarIntegracoes, opcoes, args),

    /** `GET /v1/admin/integrations/chatwoot/channels/whatsapp` — os números conectados. */
    /** `POST .../chatwoot/channels/web-chat` — um chat no site. */
    criarWebChat: (args: Argumentos<typeof ROTAS.criarWebChat>) =>
      chamar(ROTAS.criarWebChat, opcoes, args),

    /** `POST .../chatwoot/channels/api` — um canal que outro sistema alimenta. */
    criarCanalDeApi: (args: Argumentos<typeof ROTAS.criarCanalDeApi>) =>
      chamar(ROTAS.criarCanalDeApi, opcoes, args),

    /** `DELETE .../chatwoot/channels/{id}` — desconecta o canal (com step-up). */
    desconectarCanal: (args: Argumentos<typeof ROTAS.desconectarCanal>) =>
      chamar(ROTAS.desconectarCanal, opcoes, args),

    /** `GET .../chatwoot/channels/{id}/atendentes` — quem atende o canal. */
    atendentesDoCanal: (args: Argumentos<typeof ROTAS.atendentesDoCanal>) =>
      chamar(ROTAS.atendentesDoCanal, opcoes, args),

    /** `PUT .../chatwoot/channels/{id}/atendentes` — troca quem atende o canal. */
    definirAtendentes: (args: Argumentos<typeof ROTAS.definirAtendentes>) =>
      chamar(ROTAS.definirAtendentes, opcoes, args),

    /** `GET /v1/notificacoes/mencoes` — "Menções para mim". */
    mencoesParaMim: (args?: Argumentos<typeof ROTAS.mencoesParaMim>) =>
      chamar(ROTAS.mencoesParaMim, opcoes, args),

    /** `GET /v1/conversations/{id}/mencionaveis` — quem cabe no "@" da nota interna. */
    mencionaveis: (args: Argumentos<typeof ROTAS.mencionaveis>) =>
      chamar(ROTAS.mencionaveis, opcoes, args),

    /** `GET /v1/suporte/chat` — o balão de suporte, com a identidade assinada. */
    chatDeSuporte: (args?: Argumentos<typeof ROTAS.chatDeSuporte>) =>
      chamar(ROTAS.chatDeSuporte, opcoes, args),

    /** `POST /v1/cep/consulta` — o endereço de um CEP, perguntado pelo servidor (03/11/2026). */
    enderecoDoCep: (args: Argumentos<typeof ROTAS.enderecoDoCep>) =>
      chamar(ROTAS.enderecoDoCep, opcoes, args),

    // ── Integrações de dados (#922): só os devs da Cliqfy ───────────────────

    /** `GET /v1/integracoes-de-dados/conexoes` — as conexões aos bancos do cliente (só a Cliqfy). */
    conexoesDeDados: (args?: Argumentos<typeof ROTAS.conexoesDeDados>) =>
      chamar(ROTAS.conexoesDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/conexoes` — cria a conexão; a senha não volta. */
    criarConexaoDeDados: (args: Argumentos<typeof ROTAS.criarConexaoDeDados>) =>
      chamar(ROTAS.criarConexaoDeDados, opcoes, args),

    /** `GET /v1/integracoes-de-dados/conexoes/{id}` — uma conexão. */
    conexaoDeDados: (args: Argumentos<typeof ROTAS.conexaoDeDados>) =>
      chamar(ROTAS.conexaoDeDados, opcoes, args),

    /** `PATCH /v1/integracoes-de-dados/conexoes/{id}` — edita; sem senha, a atual fica. */
    editarConexaoDeDados: (args: Argumentos<typeof ROTAS.editarConexaoDeDados>) =>
      chamar(ROTAS.editarConexaoDeDados, opcoes, args),

    /** `DELETE /v1/integracoes-de-dados/conexoes/{id}` — remove; em uso, 409. */
    removerConexaoDeDados: (args: Argumentos<typeof ROTAS.removerConexaoDeDados>) =>
      chamar(ROTAS.removerConexaoDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/conexoes/{id}/teste` — testa etapa por etapa. */
    testarConexaoDeDados: (args: Argumentos<typeof ROTAS.testarConexaoDeDados>) =>
      chamar(ROTAS.testarConexaoDeDados, opcoes, args),

    /** `GET /v1/integracoes-de-dados/conexoes/{id}/mapa` — tabelas e colunas. */
    mapaDoBanco: (args: Argumentos<typeof ROTAS.mapaDoBanco>) =>
      chamar(ROTAS.mapaDoBanco, opcoes, args),

    /** `POST /v1/integracoes-de-dados/conexoes/{id}/mapa/atualizar` — relê o mapa. */
    atualizarMapaDoBanco: (args: Argumentos<typeof ROTAS.atualizarMapaDoBanco>) =>
      chamar(ROTAS.atualizarMapaDoBanco, opcoes, args),

    /** `GET /v1/integracoes-de-dados/operacoes` — as operações SQL, por finalidade. */
    operacoesDeDados: (args?: Argumentos<typeof ROTAS.operacoesDeDados>) =>
      chamar(ROTAS.operacoesDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/operacoes` — cria a operação SQL. */
    criarOperacaoDeDados: (args: Argumentos<typeof ROTAS.criarOperacaoDeDados>) =>
      chamar(ROTAS.criarOperacaoDeDados, opcoes, args),

    /** `GET /v1/integracoes-de-dados/operacoes/{id}` — uma operação. */
    operacaoDeDados: (args: Argumentos<typeof ROTAS.operacaoDeDados>) =>
      chamar(ROTAS.operacaoDeDados, opcoes, args),

    /** `PATCH /v1/integracoes-de-dados/operacoes/{id}` — versão nova. */
    editarOperacaoDeDados: (args: Argumentos<typeof ROTAS.editarOperacaoDeDados>) =>
      chamar(ROTAS.editarOperacaoDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/operacoes/{id}/teste` — testa (escrita desfeita). */
    testarOperacaoDeDados: (args: Argumentos<typeof ROTAS.testarOperacaoDeDados>) =>
      chamar(ROTAS.testarOperacaoDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/operacoes/{id}/execucao` — executa ao vivo. */
    executarOperacaoDeDados: (args: Argumentos<typeof ROTAS.executarOperacaoDeDados>) =>
      chamar(ROTAS.executarOperacaoDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/operacoes/{id}/aprovar` — aprova (outro dev). */
    aprovarOperacaoDeDados: (args: Argumentos<typeof ROTAS.aprovarOperacaoDeDados>) =>
      chamar(ROTAS.aprovarOperacaoDeDados, opcoes, args),

    /** `POST /v1/integracoes-de-dados/operacoes/{id}/recusar` — recusa. */
    recusarOperacaoDeDados: (args: Argumentos<typeof ROTAS.recusarOperacaoDeDados>) =>
      chamar(ROTAS.recusarOperacaoDeDados, opcoes, args),

    /** `GET /v1/integracoes-de-dados/operacoes/{id}/historico` — versões e decisões. */
    historicoDaOperacaoDeDados: (args: Argumentos<typeof ROTAS.historicoDaOperacaoDeDados>) =>
      chamar(ROTAS.historicoDaOperacaoDeDados, opcoes, args),

    // ── Automações (fase 2, #932) ───────────────────────────────────────────

    /** `GET /v1/automacoes/blocos` — o catálogo de blocos do editor. */
    blocosDoEditor: (args?: Argumentos<typeof ROTAS.blocosDoEditor>) =>
      chamar(ROTAS.blocosDoEditor, opcoes, args),

    /** `GET /v1/automacoes/modelos` — os modelos prontos. */
    modelosDeAutomacao: (args?: Argumentos<typeof ROTAS.modelosDeAutomacao>) =>
      chamar(ROTAS.modelosDeAutomacao, opcoes, args),

    /** `GET /v1/automacoes/modelos/{chave}` — um modelo, com o esquema. */
    modeloDeAutomacao: (args: Argumentos<typeof ROTAS.modeloDeAutomacao>) =>
      chamar(ROTAS.modeloDeAutomacao, opcoes, args),

    /** `GET /v1/automacoes/execucoes` — as execuções. */
    execucoesDasAutomacoes: (args?: Argumentos<typeof ROTAS.execucoesDasAutomacoes>) =>
      chamar(ROTAS.execucoesDasAutomacoes, opcoes, args),

    /** `GET /v1/automacoes/execucoes/{id}` — uma execução. */
    execucaoDaAutomacao: (args: Argumentos<typeof ROTAS.execucaoDaAutomacao>) =>
      chamar(ROTAS.execucaoDaAutomacao, opcoes, args),

    /** `GET /v1/automacoes/em-espera` — as instâncias em espera. */
    automacoesEmEspera: (args?: Argumentos<typeof ROTAS.automacoesEmEspera>) =>
      chamar(ROTAS.automacoesEmEspera, opcoes, args),

    /** `POST /v1/automacoes/em-espera/{id}/cancelar` — cancela uma instância em espera. */
    cancelarEsperaDaAutomacao: (args: Argumentos<typeof ROTAS.cancelarEsperaDaAutomacao>) =>
      chamar(ROTAS.cancelarEsperaDaAutomacao, opcoes, args),

    /** `GET /v1/automacoes/painel` — o painel. */
    painelDasAutomacoes: (args?: Argumentos<typeof ROTAS.painelDasAutomacoes>) =>
      chamar(ROTAS.painelDasAutomacoes, opcoes, args),

    /** `POST /v1/automacoes/validar` — valida sem salvar. */
    validarAutomacao: (args: Argumentos<typeof ROTAS.validarAutomacao>) =>
      chamar(ROTAS.validarAutomacao, opcoes, args),

    /** `GET /v1/automacoes` — as automações. */
    automacoes: (args?: Argumentos<typeof ROTAS.automacoes>) =>
      chamar(ROTAS.automacoes, opcoes, args),

    /** `POST /v1/automacoes` — cria no editor. */
    criarAutomacao: (args: Argumentos<typeof ROTAS.criarAutomacao>) =>
      chamar(ROTAS.criarAutomacao, opcoes, args),

    /** `POST /v1/automacoes/a-partir-de-modelo` — monta as automações de um modelo. */
    criarAutomacaoDoModelo: (args: Argumentos<typeof ROTAS.criarAutomacaoDoModelo>) =>
      chamar(ROTAS.criarAutomacaoDoModelo, opcoes, args),

    /** `PATCH /v1/automacoes/{id}/nome-e-descricao` — muda o nome e a descrição. */
    identificarAutomacao: (args: Argumentos<typeof ROTAS.identificarAutomacao>) =>
      chamar(ROTAS.identificarAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/descartar-pendente` — descarta a alteração pendente. */
    descartarPendenteDaAutomacao: (args: Argumentos<typeof ROTAS.descartarPendenteDaAutomacao>) =>
      chamar(ROTAS.descartarPendenteDaAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/arquivar` — arquiva. */
    arquivarAutomacao: (args: Argumentos<typeof ROTAS.arquivarAutomacao>) =>
      chamar(ROTAS.arquivarAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/desarquivar` — desarquiva (volta para rascunho). */
    desarquivarAutomacao: (args: Argumentos<typeof ROTAS.desarquivarAutomacao>) =>
      chamar(ROTAS.desarquivarAutomacao, opcoes, args),
    /** `DELETE /v1/automacoes/{id}` — exclui de vez (a ativa é recusada). */
    removerAutomacao: (args: Argumentos<typeof ROTAS.removerAutomacao>) =>
      chamar(ROTAS.removerAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/simular` — simula sem efeito real. */
    simularAutomacao: (args: Argumentos<typeof ROTAS.simularAutomacao>) =>
      chamar(ROTAS.simularAutomacao, opcoes, args),

    /** `GET /v1/automacoes/{id}` — uma automação. */
    automacao: (args: Argumentos<typeof ROTAS.automacao>) => chamar(ROTAS.automacao, opcoes, args),

    /** `PATCH /v1/automacoes/{id}` — edita; `versao` velha é 409. */
    editarAutomacao: (args: Argumentos<typeof ROTAS.editarAutomacao>) =>
      chamar(ROTAS.editarAutomacao, opcoes, args),
    /** `PATCH /v1/automacoes/{id}/configuracao-do-modelo` — edita a de modelo pronto. */
    editarAutomacaoDoModelo: (args: Argumentos<typeof ROTAS.editarAutomacaoDoModelo>) =>
      chamar(ROTAS.editarAutomacaoDoModelo, opcoes, args),

    /** `POST /v1/automacoes/{id}/enviar-para-homologacao` — rascunho → em homologação. */
    enviarAutomacaoParaHomologacao: (
      args: Argumentos<typeof ROTAS.enviarAutomacaoParaHomologacao>,
    ) => chamar(ROTAS.enviarAutomacaoParaHomologacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/ativar` — ativa (ou promove a pendente). */
    ativarAutomacao: (args: Argumentos<typeof ROTAS.ativarAutomacao>) =>
      chamar(ROTAS.ativarAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/pausar` — pausa. */
    pausarAutomacao: (args: Argumentos<typeof ROTAS.pausarAutomacao>) =>
      chamar(ROTAS.pausarAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/voltar-para-rascunho` — devolve para rascunho. */
    voltarAutomacaoParaRascunho: (args: Argumentos<typeof ROTAS.voltarAutomacaoParaRascunho>) =>
      chamar(ROTAS.voltarAutomacaoParaRascunho, opcoes, args),

    /** `GET /v1/automacoes/{id}/historico` — o histórico de versões. */
    historicoDaAutomacao: (args: Argumentos<typeof ROTAS.historicoDaAutomacao>) =>
      chamar(ROTAS.historicoDaAutomacao, opcoes, args),

    /** `POST /v1/automacoes/{id}/restaurar` — restaura uma versão. */
    restaurarAutomacao: (args: Argumentos<typeof ROTAS.restaurarAutomacao>) =>
      chamar(ROTAS.restaurarAutomacao, opcoes, args),

    // ── Listas (#922): lidas ao vivo do banco do cliente ─────────────────────

    /** `GET /v1/listas` — as listas da organização, por finalidade. */
    listas: (args?: Argumentos<typeof ROTAS.listas>) => chamar(ROTAS.listas, opcoes, args),

    /** `POST /v1/listas` — monta a lista, em rascunho (só a Cliqfy). */
    criarLista: (args: Argumentos<typeof ROTAS.criarLista>) =>
      chamar(ROTAS.criarLista, opcoes, args),

    /** `GET /v1/listas/pedidos` — os pedidos de lista. */
    pedidosDeLista: (args?: Argumentos<typeof ROTAS.pedidosDeLista>) =>
      chamar(ROTAS.pedidosDeLista, opcoes, args),

    /** `POST /v1/listas/pedidos` — pede uma lista nova à Cliqfy. */
    pedirLista: (args: Argumentos<typeof ROTAS.pedirLista>) =>
      chamar(ROTAS.pedirLista, opcoes, args),

    /** `POST /v1/listas/pedidos/{id}/atender` — atende com a lista montada. */
    atenderPedidoDeLista: (args: Argumentos<typeof ROTAS.atenderPedidoDeLista>) =>
      chamar(ROTAS.atenderPedidoDeLista, opcoes, args),

    /** `POST /v1/listas/pedidos/{id}/recusar` — recusa com o motivo. */
    recusarPedidoDeLista: (args: Argumentos<typeof ROTAS.recusarPedidoDeLista>) =>
      chamar(ROTAS.recusarPedidoDeLista, opcoes, args),

    /** `GET /v1/listas/{id}` — uma lista. */
    lista: (args: Argumentos<typeof ROTAS.lista>) => chamar(ROTAS.lista, opcoes, args),

    /** `PATCH /v1/listas/{id}` — troca a definição; `versao` velha é 409. */
    editarLista: (args: Argumentos<typeof ROTAS.editarLista>) =>
      chamar(ROTAS.editarLista, opcoes, args),

    /** `DELETE /v1/listas/{id}` — arquiva a lista. */
    arquivarLista: (args: Argumentos<typeof ROTAS.arquivarLista>) =>
      chamar(ROTAS.arquivarLista, opcoes, args),

    /** `POST /v1/listas/{id}/publicar` — libera a lista para quem atende. */
    publicarLista: (args: Argumentos<typeof ROTAS.publicarLista>) =>
      chamar(ROTAS.publicarLista, opcoes, args),

    /** `POST /v1/listas/{id}/leitura` — a prévia ao vivo, como tabela. */
    lerLista: (args: Argumentos<typeof ROTAS.lerLista>) => chamar(ROTAS.lerLista, opcoes, args),

    /** `GET /v1/listas/{id}/itens` — os itens do dropdown, ao vivo. */
    itensDaLista: (args: Argumentos<typeof ROTAS.itensDaLista>) =>
      chamar(ROTAS.itensDaLista, opcoes, args),

    /** `GET /v1/lista-de-espera/automacoes` — se a pessoa já está na lista. */
    lugarNaListaDasAutomacoes: (args?: Argumentos<typeof ROTAS.lugarNaListaDasAutomacoes>) =>
      chamar(ROTAS.lugarNaListaDasAutomacoes, opcoes, args),

    /** `POST /v1/lista-de-espera/automacoes` — entra na lista; repetir não duplica. */
    entrarNaListaDasAutomacoes: (args?: Argumentos<typeof ROTAS.entrarNaListaDasAutomacoes>) =>
      chamar(ROTAS.entrarNaListaDasAutomacoes, opcoes, args),

    /** `GET /v1/operacao/lista-de-espera/automacoes` — quem pediu, em todas as organizações. */
    listaDeEsperaDasAutomacoes: (args?: Argumentos<typeof ROTAS.listaDeEsperaDasAutomacoes>) =>
      chamar(ROTAS.listaDeEsperaDasAutomacoes, opcoes, args),

    /** `GET /v1/operacao/super-admins` — quem é Super Admin (ADR-0025). */
    superAdmins: (args?: Argumentos<typeof ROTAS.superAdmins>) =>
      chamar(ROTAS.superAdmins, opcoes, args),

    /** `POST /v1/operacao/super-admins` — dar o papel pelo e-mail. */
    concederSuperAdmin: (args: Argumentos<typeof ROTAS.concederSuperAdmin>) =>
      chamar(ROTAS.concederSuperAdmin, opcoes, args),

    /** `DELETE /v1/operacao/super-admins/{pessoaId}` — tirar o papel de outra pessoa. */
    revogarSuperAdmin: (args: Argumentos<typeof ROTAS.revogarSuperAdmin>) =>
      chamar(ROTAS.revogarSuperAdmin, opcoes, args),

    /** `GET /v1/operacao/planos` — o catálogo de planos (#766). */
    planos: (args?: Argumentos<typeof ROTAS.planos>) => chamar(ROTAS.planos, opcoes, args),

    /** `POST /v1/operacao/planos` — um plano novo no catálogo. */
    criarPlano: (args: Argumentos<typeof ROTAS.criarPlano>) =>
      chamar(ROTAS.criarPlano, opcoes, args),

    /** `PATCH /v1/operacao/planos/{id}` — editar ou desativar um plano. */
    editarPlano: (args: Argumentos<typeof ROTAS.editarPlano>) =>
      chamar(ROTAS.editarPlano, opcoes, args),

    /** `GET /v1/operacao/organizacoes` — todas as organizações, com o contrato e o uso (#767). */
    organizacoesNaGestao: (args?: Argumentos<typeof ROTAS.organizacoesNaGestao>) =>
      chamar(ROTAS.organizacoesNaGestao, opcoes, args),

    /** `GET /v1/operacao/organizacoes/{id}` — uma organização na gestão. */
    organizacaoNaGestao: (args: Argumentos<typeof ROTAS.organizacaoNaGestao>) =>
      chamar(ROTAS.organizacaoNaGestao, opcoes, args),

    /** `GET /v1/operacao/organizacoes/{id}/historico` — o histórico da organização. */
    historicoDaOrganizacao: (args: Argumentos<typeof ROTAS.historicoDaOrganizacao>) =>
      chamar(ROTAS.historicoDaOrganizacao, opcoes, args),

    /** `PATCH /v1/operacao/organizacoes/{id}/contrato` — alterar o contrato da organização. */
    alterarContratoDaOrganizacao: (args: Argumentos<typeof ROTAS.alterarContratoDaOrganizacao>) =>
      chamar(ROTAS.alterarContratoDaOrganizacao, opcoes, args),

    /** `PATCH /v1/operacao/organizacoes/{id}/cadastro` — alterar o cadastro da organização. */
    alterarCadastroDaOrganizacao: (args: Argumentos<typeof ROTAS.alterarCadastroDaOrganizacao>) =>
      chamar(ROTAS.alterarCadastroDaOrganizacao, opcoes, args),

    /** `POST /v1/operacao/organizacoes/{id}/retomar` — retomar a criação com o pedido guardado. */
    retomarCriacaoDaOrganizacao: (args: Argumentos<typeof ROTAS.retomarCriacaoDaOrganizacao>) =>
      chamar(ROTAS.retomarCriacaoDaOrganizacao, opcoes, args),

    /** `GET /v1/admin/integrations/chatwoot/channels` — todos os canais conectados. */
    canaisConectados: (args?: Argumentos<typeof ROTAS.canaisConectados>) =>
      chamar(ROTAS.canaisConectados, opcoes, args),

    canaisDeWhatsApp: (args?: Argumentos<typeof ROTAS.canaisDeWhatsApp>) =>
      chamar(ROTAS.canaisDeWhatsApp, opcoes, args),

    /** `POST /v1/admin/integrations/meta/authorize` — começa a conexão da Meta. */
    autorizarMeta: (args: Argumentos<typeof ROTAS.autorizarMeta>) =>
      chamar(ROTAS.autorizarMeta, opcoes, args),

    /**
     * `POST /v1/admin/integrations/meta/pages` — lista o que a autorização alcança.
     *
     * Devolve a REFERÊNCIA da autorização, nunca o token: ele abre todas as
     * páginas que a pessoa administra.
     */
    paginasDaMeta: (args: Argumentos<typeof ROTAS.paginasDaMeta>) =>
      chamar(ROTAS.paginasDaMeta, opcoes, args),

    /** `POST /v1/admin/integrations/meta/pages/connect` — liga a página escolhida. */
    conectarPaginaDaMeta: (args: Argumentos<typeof ROTAS.conectarPaginaDaMeta>) =>
      chamar(ROTAS.conectarPaginaDaMeta, opcoes, args),

    /**
     * `POST .../channels/whatsapp/signup` — o caminho principal do WhatsApp.
     *
     * Recebe o resultado do popup da Meta. A revenda não digita nada.
     */
    conectarWhatsAppPorSignup: (args: Argumentos<typeof ROTAS.conectarWhatsAppPorSignup>) =>
      chamar(ROTAS.conectarWhatsAppPorSignup, opcoes, args),

    /**
     * `POST /v1/admin/integrations/chatwoot/channels/whatsapp` — conecta um número.
     *
     * Exige step-up. O token é conferido contra a Meta na própria criação.
     */
    conectarWhatsApp: (args: Argumentos<typeof ROTAS.conectarWhatsApp>) =>
      chamar(ROTAS.conectarWhatsApp, opcoes, args),

    /**
     * `POST /v1/admin/integrations/chatwoot/webhook` — liga o Chatwoot ao ingress.
     *
     * Exige step-up. A URL devolvida contém o `hook_key` e volta uma única vez.
     */
    ligarWebhookDoChatwoot: (args: Argumentos<typeof ROTAS.ligarWebhookDoChatwoot>) =>
      chamar(ROTAS.ligarWebhookDoChatwoot, opcoes, args),

    /**
     * `GET /v1/admin/integrations/catalog` — o que cada plataforma exige (D11).
     *
     * É daqui que a tela monta o formulário de configuração de um portal:
     * quais campos, de que tipo, e onde a revenda encontra cada um. Nenhum
     * valor preenchido sai por esta rota.
     */
    catalogoDePlataformas: (args: Argumentos<typeof ROTAS.catalogoDePlataformas>) =>
      chamar(ROTAS.catalogoDePlataformas, opcoes, args),

    /**
     * `POST /v1/admin/integrations/{provider}/connections` (D11).
     *
     * Exige concessão de step-up vigente. Sem ela vem 403 com a extensão
     * `step_up`, que diz o que a tela deve pedir antes de tentar de novo.
     */
    configurarIntegracao: (args: Argumentos<typeof ROTAS.configurarIntegracao>) =>
      chamar(ROTAS.configurarIntegracao, opcoes, args),

    /** `POST /v1/admin/operacao/reconciliar` — a ação segura do painel (G04). */
    forcarReconciliacao: (args?: Argumentos<typeof ROTAS.forcarReconciliacao>) =>
      chamar(ROTAS.forcarReconciliacao, opcoes, args),

    /* ── Direitos do titular (D16, art. 18) ────────────────────────────── */

    /**
     * `POST /v1/titular/pedidos` — registra e NÃO executa.
     *
     * O prazo de 15 dias começa a correr aqui. Separar registro de execução é
     * o que permite o pedido existir enquanto o titular ainda não foi
     * identificado — recusar por não achar perderia o prazo.
     */
    registrarPedidoDoTitular: (args: Argumentos<typeof ROTAS.registrarPedidoDoTitular>) =>
      chamar(ROTAS.registrarPedidoDoTitular, opcoes, args),

    /** `GET /v1/titular/pedidos` — a fila, do mais urgente ao menos. */
    listarPedidosDoTitular: (args?: Argumentos<typeof ROTAS.listarPedidosDoTitular>) =>
      chamar(ROTAS.listarPedidosDoTitular, opcoes, args),

    /** `GET /v1/titular/pedidos/{id}` — com os recibos por sistema. */
    buscarPedidoDoTitular: (args: Argumentos<typeof ROTAS.buscarPedidoDoTitular>) =>
      chamar(ROTAS.buscarPedidoDoTitular, opcoes, args),

    /** `POST /v1/titular/pedidos/{id}/executar` — exige step-up. */
    executarPedidoDoTitular: (args: Argumentos<typeof ROTAS.executarPedidoDoTitular>) =>
      chamar(ROTAS.executarPedidoDoTitular, opcoes, args),

    /** `POST /v1/titular/pedidos/{id}/recusar` — motivo obrigatório (art. 18, §4º). */
    recusarPedidoDoTitular: (args: Argumentos<typeof ROTAS.recusarPedidoDoTitular>) =>
      chamar(ROTAS.recusarPedidoDoTitular, opcoes, args),

    /** `GET /v1/titular/{clienteId}/exportacao` — para conferir antes de responder. */
    exportarTitular: (args: Argumentos<typeof ROTAS.exportarTitular>) =>
      chamar(ROTAS.exportarTitular, opcoes, args),

    /* ── Ciclo de vida da revenda (G01) — só o Super Admin ─────────────── */

    /** `POST /v1/operacao/revendas` — idempotente por `slug`; retoma a saga. */
    provisionarRevenda: (args: Argumentos<typeof ROTAS.provisionarRevenda>) =>
      chamar(ROTAS.provisionarRevenda, opcoes, args),

    /** `POST /v1/operacao/revendas/{id}/encerrar` — reversível (decisão B2). */
    completarRevenda: (args: Argumentos<typeof ROTAS.completarRevenda>) =>
      chamar(ROTAS.completarRevenda, opcoes, args),
    encerrarRevenda: (args: Argumentos<typeof ROTAS.encerrarRevenda>) =>
      chamar(ROTAS.encerrarRevenda, opcoes, args),

    /** `POST /v1/operacao/revendas/{id}/reabrir` — o contrário exato. */
    reabrirRevenda: (args: Argumentos<typeof ROTAS.reabrirRevenda>) =>
      chamar(ROTAS.reabrirRevenda, opcoes, args),

    /**
     * `POST /v1/operacao/revendas/{id}/excluir` — irreversível.
     *
     * Exige concessão de step-up de `tenant.excluir` com o id da revenda como
     * alvo, e que ela já esteja encerrada. Sem a concessão vem 403 com a
     * extensão que diz o que a tela precisa pedir antes de tentar de novo.
     */
    excluirRevenda: (args: Argumentos<typeof ROTAS.excluirRevenda>) =>
      chamar(ROTAS.excluirRevenda, opcoes, args),
  };
}

export type ClienteDaApi = ReturnType<typeof criarCliente>;
